import * as flatbuffers from 'flatbuffers'
import { tbs } from './engine/bindings/typescript/engine'

const debugWasmUrl = new URL('./engine/bin/engine_debug.wasm', import.meta.url)
const releaseWasmUrl = new URL('./engine/bin/engine.wasm', import.meta.url)

const wasmConfig = {
  url: import.meta.env.DEV ? debugWasmUrl : releaseWasmUrl,
  label: import.meta.env.DEV ? 'debug' : 'release',
}

const BYTES_FOR_LENGTH = 4

type EngineExports = {
  memory: WebAssembly.Memory
  wasm_alloc: (size: number) => number
  wasm_free: (ptr: number, size: number) => void
  run_map_script: (out_len_ptr: number) => number
  create_game: (ptr: number, len: number) => number
  apply_action: (handle: number, ptr: number, len: number) => number
  get_full_state: (handle: number, out_len_ptr: number) => number
}

export interface EngineStateSnapshot {
  bytes: Uint8Array
  length: number
  payloadType: tbs.EnginePayload
  payloadName: string
  message: tbs.EngineMessage
  messageObject: tbs.EngineMessageT
}

async function fetchEngineBinary(url: URL): Promise<ArrayBuffer> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`[engine-wrapper] failed to download ${wasmConfig.label} wasm (${response.status} ${response.statusText})`)
  }
  return response.arrayBuffer()
}

export class EngineInstance {
  private constructor(private readonly exports: EngineExports) {}

  static async create(): Promise<EngineInstance> {
    console.info(`[engine-wrapper] loading ${wasmConfig.label} wasm from ${wasmConfig.url.href}`)
    const wasmBinary = await fetchEngineBinary(wasmConfig.url)
    const { instance } = await WebAssembly.instantiate(wasmBinary, {
      env: {},
    })
    return new EngineInstance(instance.exports as EngineExports)
  }

  runMapScript(): EngineStateSnapshot {
    const outPtr = this.allocLengthBuffer()
    const statePtr = this.exports.run_map_script(outPtr)
    const stateLen = this.readUint32(outPtr)
    const bytes = this.readBytes(statePtr, stateLen)
    this.free(outPtr, BYTES_FOR_LENGTH)
    this.free(statePtr, stateLen)
    return this.decodeState(bytes)
  }

  createGameFromState(stateBytes: Uint8Array): number {
    const ptr = this.writeBytes(stateBytes)
    const handle = this.exports.create_game(ptr, stateBytes.length)
    this.free(ptr, stateBytes.length)
    if (handle <= 0) {
      throw new Error(`[engine-wrapper] create_game returned invalid handle ${handle}`)
    }
    return handle
  }

  applyAction(handle: number, actionBytes: Uint8Array): number {
    const ptr = this.writeBytes(actionBytes)
    const result = this.exports.apply_action(handle, ptr, actionBytes.length)
    this.free(ptr, actionBytes.length)
    return result
  }

  getFullState(handle: number): EngineStateSnapshot {
    const outPtr = this.allocLengthBuffer()
    const statePtr = this.exports.get_full_state(handle, outPtr)
    const stateLen = this.readUint32(outPtr)
    const bytes = this.readBytes(statePtr, stateLen)
    this.free(outPtr, BYTES_FOR_LENGTH)
    this.free(statePtr, stateLen)
    return this.decodeState(bytes)
  }

  private allocLengthBuffer(): number {
    return this.exports.wasm_alloc(BYTES_FOR_LENGTH)
  }

  private readUint32(ptr: number): number {
    const view = new DataView(this.exports.memory.buffer)
    return view.getUint32(ptr, true)
  }

  private readBytes(ptr: number, len: number): Uint8Array {
    return new Uint8Array(this.exports.memory.buffer, ptr, len).slice()
  }

  private writeBytes(source: Uint8Array): number {
    const ptr = this.exports.wasm_alloc(source.length)
    new Uint8Array(this.exports.memory.buffer, ptr, source.length).set(source)
    return ptr
  }

  private free(ptr: number, len: number): void {
    this.exports.wasm_free(ptr, len)
  }

  private decodeState(bytes: Uint8Array): EngineStateSnapshot {
    const message = tbs.EngineMessage.getRootAsEngineMessage(new flatbuffers.ByteBuffer(bytes))
    const payloadType = message.payloadType()
    const payloadName = tbs.EnginePayload[payloadType] ?? `payload-${payloadType}`
    return {
      bytes,
      length: bytes.length,
      payloadType,
      payloadName,
      message,
      messageObject: message.unpack(),
    }
  }
}
